/** Intervalo de almoço/pausa (ex: 12:00-13:00). Quando presente, o estabelecimento fecha nesse período. */
export interface HorarioIntervalo {
  inicio: string; // "12:00"
  fim: string; // "13:00"
}

/** Horário de um dia da semana (ex: Segunda 08:00-18:00). null = fechado. */
export interface HorarioDia {
  abertura: string; // "08:00"
  fechamento: string; // "18:00"
  /** Opcional: fechar para almoço/intervalo. Ex: 12:00-13:00 */
  intervalo?: HorarioIntervalo;
}

export type DiaSemana = 'seg' | 'ter' | 'qua' | 'qui' | 'sex' | 'sab' | 'dom';

/** Horário semanal: seg a dom. null = fechado. */
export interface HorarioSemanal {
  seg: HorarioDia | null;
  ter: HorarioDia | null;
  qua: HorarioDia | null;
  qui: HorarioDia | null;
  sex: HorarioDia | null;
  sab: HorarioDia | null;
  dom: HorarioDia | null;
}

/** Exceção para um dia específico (feriado, horário especial). Data em YYYY-MM-DD. */
export interface ExcecaoDia {
  tipo: 'fechado' | 'horario';
  abertura?: string;
  fechamento?: string;
}

/** Responsável vinculado ao estabelecimento (usuário COMERCIO_RESTAURANTE). */
export interface EstabelecimentoResponsavel {
  userId: string;
  name: string;
  email?: string;
  phone?: string;
}

/** Status ativo/inativo (igual Experiências). */
export type StatusPadraoEstabelecimento = 'ativado' | 'desativado';

/** Destaque na listagem (mesma semântica das notícias). */
export type EstabelecimentoLayoutType = 'normal' | 'matéria_principal' | 'slide_principal';

/** Origem do cadastro do estabelecimento. */
export type EstabelecimentoSource = 'manual' | 'google_import';

/** Desconto morador: percentual, valor em reais ou texto descritivo. */
export type DescontoMoradorModo = 'percent' | 'currency' | 'text';

export interface Estabelecimento {
  id: string;
  nome: string;
  /** Linha curta opcional (cards / vitrine). */
  subtitulo?: string;
  /** Resumo curto obrigatório no admin (listagens / SEO). */
  resumo?: string;
  /** Descrição do estabelecimento */
  descricao?: string;
  /** CNPJ - opcional quando source = google_import */
  cnpj?: string;
  /** ID da cidade onde está localizado (obrigatório - somente cidades cadastradas) */
  cityId?: string;
  /** Endereço completo do estabelecimento */
  endereco?: string;
  /** Coordenadas para exibição no mapa */
  lat?: number;
  lng?: number;
  /** Categoria (mesmas de notícia). Obrigatório. */
  categoryId?: string;
  /** Primeira subcategoria (espelho de `subcategoryIds[0]`). */
  subcategoryId?: string;
  /** Uma ou mais subcategorias. */
  subcategoryIds?: string[];
  responsaveis: EstabelecimentoResponsavel[];
  statusPadrao: StatusPadraoEstabelecimento;
  periodosStatus?: {
    de: string;
    ate: string;
    tipo: 'ativado' | 'desativado' | 'naviex' | 'principal' | 'categoria';
  }[];
  /** Histórico de agendamentos (mesma semântica que notícias). */
  statusProgramadoHistorico?: {
    quando: string;
    dia: string;
    tipo: 'ativado' | 'desativado' | 'naviex' | 'principal' | 'categoria';
    modo?: 'periodo' | 'permanente';
    ate?: string;
  }[];
  /** Destaque “principal” (ex.: home / vitrine). Requer `layoutExpiresAt` quando ativo. */
  layoutType?: EstabelecimentoLayoutType;
  layoutExpiresAt?: string;
  destaqueSubcategoria?: boolean;
  destaqueSubcategoriaExpiresAt?: string;
  destaqueNaviex?: boolean;
  destaqueNaviexExpiresAt?: string;
  horarioSemanal: HorarioSemanal;
  /** Exceções por data (YYYY-MM-DD). Ex: feriados. */
  excecoesCalendario: Record<string, ExcecaoDia>;
  createdAt: string;
  updatedAt: string;
  /** ID do lugar no Google Places (quando importado) */
  googlePlaceId?: string;
  /** Origem do cadastro */
  source?: EstabelecimentoSource;
  /** Avaliação do Google (0-5) */
  rating?: number;
  /** Quantidade de avaliações */
  totalRatings?: number;
  /** Endereço formatado (ex.: do Google) */
  formattedAddress?: string;
  /** URLs/referências s3: das fotos (até 8). SEO: NaviExperience - {nome} - Foto N */
  fotos?: string[];
  /** Tags (mesma lista global que experiências; opcional). */
  tags?: string[];
  /** Desconto e parceria: texto livre (tipo de parceria, condições, etc.). */
  descontoParceriaDescricao?: string;
  /** Desconto para moradores (opcional). */
  descontoMoradorAtivo?: boolean;
  descontoMoradorModo?: DescontoMoradorModo;
  /** Valor quando modo é % ou R$ (texto livre, ex.: "15" ou "19,90"). */
  descontoMoradorValor?: string;
  /** Texto quando modo é descritivo. */
  descontoMoradorTexto?: string;
  /** Admin: rascunho até publicar; omitido = publicado (legado); lixeira = removido da lista principal até restaurar ao publicar. */
  publicacaoStatus?: 'rascunho' | 'publicado' | 'lixeira';
}

/** Une `subcategoryIds` e o campo legado `subcategoryId`. */
export function mergeEstabelecimentoSubcategoryIds(
  e: Pick<Estabelecimento, 'subcategoryId' | 'subcategoryIds'>
): string[] {
  const fromArr = (e.subcategoryIds ?? []).map((id) => String(id).trim()).filter(Boolean);
  const leg = e.subcategoryId?.trim();
  const out: string[] = [];
  const seen = new Set<string>();
  for (const id of fromArr) {
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  if (leg && !seen.has(leg)) {
    out.unshift(leg);
    seen.add(leg);
  }
  return out;
}

export function normalizeEstabelecimentoForStore(est: Estabelecimento): Estabelecimento {
  const ids = mergeEstabelecimentoSubcategoryIds(est);
  const {
    descontoMoradorAtivo,
    descontoMoradorModo,
    descontoMoradorValor,
    descontoMoradorTexto,
    ...rest
  } = est;
  const base: Estabelecimento = {
    ...rest,
    subcategoryIds: ids.length > 0 ? ids : undefined,
    subcategoryId: ids[0] ?? '',
  };
  if (descontoMoradorAtivo === true) {
    const modo: DescontoMoradorModo = descontoMoradorModo === 'text' || descontoMoradorModo === 'currency' ? descontoMoradorModo : 'percent';
    if (modo === 'text') {
      const t = (descontoMoradorTexto ?? '').trim();
      return {
        ...base,
        descontoMoradorAtivo: true,
        descontoMoradorModo: 'text',
        descontoMoradorTexto: t || undefined,
      };
    }
    const v = (descontoMoradorValor ?? '').trim();
    return {
      ...base,
      descontoMoradorAtivo: true,
      descontoMoradorModo: modo,
      descontoMoradorValor: v || undefined,
    };
  }
  return base;
}

const KEY_ESTABELECIMENTOS = 'naviex-admin-estabelecimentos';
const KEY_ESTABELECIMENTOS_DELETED_IDS = 'naviex-admin-estabelecimentos-deleted-ids';

const DIAS_SEMANA: DiaSemana[] = ['seg', 'ter', 'qua', 'qui', 'sex', 'sab', 'dom'];

function loadJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function saveJson(key: string, value: unknown): void {
  localStorage.setItem(key, JSON.stringify(value));
}

function getDeletedEstabelecimentoIdsSet(): Set<string> {
  const ids = loadJson<string[]>(KEY_ESTABELECIMENTOS_DELETED_IDS, [])
    .map((id) => String(id).trim())
    .filter(Boolean);
  return new Set(ids);
}

function saveDeletedEstabelecimentoIdsSet(ids: Set<string>): void {
  saveJson(KEY_ESTABELECIMENTOS_DELETED_IDS, Array.from(ids));
}

function horarioPadrao(): HorarioSemanal {
  const padrao: HorarioDia = { abertura: '08:00', fechamento: '18:00' };
  return {
    seg: padrao,
    ter: padrao,
    qua: padrao,
    qui: padrao,
    sex: padrao,
    sab: padrao,
    dom: null, // Domingo fechado
  };
}

export function getEstabelecimentos(): Estabelecimento[] {
  const deletedIds = getDeletedEstabelecimentoIdsSet();
  return loadJson<Estabelecimento[]>(KEY_ESTABELECIMENTOS, []).map((raw) =>
    normalizeEstabelecimentoForStore(raw as Estabelecimento)
  ).filter((e) => !deletedIds.has(String(e.id).trim()));
}

export function getEstabelecimentoById(id: string): Estabelecimento | null {
  return getEstabelecimentos().find((e) => e.id === id) ?? null;
}

export function addEstabelecimento(data: Omit<Estabelecimento, 'id' | 'createdAt' | 'updatedAt'>): Estabelecimento {
  const now = new Date().toISOString();
  const list = loadJson<Estabelecimento[]>(KEY_ESTABELECIMENTOS, []);
  const next = normalizeEstabelecimentoForStore({
    ...data,
    id: `est-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    cnpj: data.cnpj ?? '',
    categoryId: data.categoryId ?? '',
    subcategoryId: data.subcategoryId ?? '',
    subcategoryIds: data.subcategoryIds,
    excecoesCalendario: data.excecoesCalendario ?? {},
    createdAt: now,
    updatedAt: now,
  } as Estabelecimento);
  const deletedIds = getDeletedEstabelecimentoIdsSet();
  if (deletedIds.delete(next.id)) saveDeletedEstabelecimentoIdsSet(deletedIds);
  saveJson(KEY_ESTABELECIMENTOS, [...list, next]);
  return next;
}

export function saveEstabelecimento(est: Estabelecimento): Estabelecimento {
  const list = loadJson<Estabelecimento[]>(KEY_ESTABELECIMENTOS, []).map((raw) =>
    normalizeEstabelecimentoForStore(raw as Estabelecimento)
  );
  const idx = list.findIndex((e) => e.id === est.id);
  if (idx < 0) throw new Error('Estabelecimento não encontrado.');
  const merged: Estabelecimento = {
    ...list[idx],
    ...est,
    updatedAt: new Date().toISOString(),
  };
  if (!('periodosStatus' in est) || est.periodosStatus === undefined) {
    delete merged.periodosStatus;
  }
  const updated = normalizeEstabelecimentoForStore(merged);
  const next = [...list];
  next[idx] = updated;
  const deletedIds = getDeletedEstabelecimentoIdsSet();
  if (deletedIds.delete(updated.id)) saveDeletedEstabelecimentoIdsSet(deletedIds);
  saveJson(KEY_ESTABELECIMENTOS, next);
  return updated;
}

export function removeEstabelecimento(id: string): void {
  const normalizedId = String(id).trim();
  const deletedIds = getDeletedEstabelecimentoIdsSet();
  deletedIds.add(normalizedId);
  saveDeletedEstabelecimentoIdsSet(deletedIds);
  saveJson(
    KEY_ESTABELECIMENTOS,
    loadJson<Estabelecimento[]>(KEY_ESTABELECIMENTOS, [])
      .map((raw) => normalizeEstabelecimentoForStore(raw as Estabelecimento))
      .filter((e) => e.id !== normalizedId)
  );
}

export function isEstabelecimentoAtivo(est: Estabelecimento): boolean {
  const now = new Date();
  const hoje = now.toISOString().slice(0, 10);
  const periodos = est.periodosStatus ?? [];
  const padrao = est.statusPadrao ?? 'ativado';
  for (const p of periodos) {
    if (hoje >= p.de && hoje <= p.ate) {
      return p.tipo !== 'desativado';
    }
  }
  return padrao === 'ativado';
}

/** Formata o horário de um dia para exibição (ex: "08:00-18:00" ou "08:00-12:00, 13:00-18:00" com intervalo) */
export function formatarHorarioDia(h: HorarioDia | null): string {
  if (!h) return '';
  if (!h.intervalo) return `${h.abertura}-${h.fechamento}`;
  return `${h.abertura}-${h.intervalo.inicio}, ${h.intervalo.fim}-${h.fechamento}`;
}

export { DIAS_SEMANA, horarioPadrao };
